/**
 * Shared helpers for a member's tournament registrations / printable receipts.
 */

/** Paid / admin-confirmed and still in the field (including a pending withdraw request). */
export const isConfirmedTournamentRegistration = (r) => {
  if (!r) return false;
  const st = String(r.status || '').toLowerCase();
  if (['refunded', 'rejected', 'withdrawn'].includes(st)) return false;
  if (['pending_payment', 'payment_failed', 'cancelled', 'expired'].includes(st)) {
    return Boolean(r.payment_verified);
  }
  return Boolean(r.payment_verified)
    || st === 'pending'
    || st === 'approved'
    || st === 'withdrawal_requested';
};

/** Joined this event — includes withdrawn so the player can see refund status. */
export const isJoinedTournamentRegistration = (r) => {
  if (!r) return false;
  const st = String(r.status || '').toLowerCase();
  if (['withdrawn', 'refunded'].includes(st)) return Boolean(r.payment_verified || r.refund_id);
  return isConfirmedTournamentRegistration(r);
};

export const filterConfirmedTournamentRegistrations = (list) =>
  (Array.isArray(list) ? list : []).filter(isConfirmedTournamentRegistration);

export const filterJoinedTournamentRegistrations = (list) =>
  (Array.isArray(list) ? list : []).filter(isJoinedTournamentRegistration);

export function formatRegistrationFee(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `₱${n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Badge + refund copy for Dashboard / Events registration rows. */
export function tournamentRegistrationStatusMeta(reg) {
  const st = String(reg?.status || '').toLowerCase();
  const refundAmt = formatRegistrationFee(reg?.refund_amount);
  if (st === 'withdrawal_requested') {
    return {
      status: st,
      label: 'Withdrawal requested',
      badgeClass: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
      hint: 'Your slot is still held until the organizer confirms.',
      canWithdraw: false,
    };
  }
  if (st === 'withdrawn' || st === 'refunded') {
    return {
      status: st,
      label: reg?.refund_id ? 'Removed · refund recorded' : 'Withdrawn',
      badgeClass: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      hint: refundAmt
        ? `Refund recorded ${refundAmt}. Collect the payout from the organizer.`
        : 'Collect the payout from the organizer.',
      refundId: reg?.refund_id || '',
      refundAmountLabel: refundAmt,
      canWithdraw: false,
    };
  }
  if (st === 'approved') {
    return {
      status: st,
      label: 'Approved',
      badgeClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
      hint: '',
      canWithdraw: Boolean(reg?.can_request_withdraw),
    };
  }
  return {
    status: st,
    label: reg?.registration_status || 'Pending verification',
    badgeClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200',
    hint: '',
    canWithdraw: Boolean(reg?.can_request_withdraw),
  };
}
